import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { eliminarProfesionalAgendaApi, obtenerProfesionalesApi } from '../services/api';
import FormularioProfesional, { TIPOS_PROFESIONAL, etiquetaTipo } from '../components/FormularioProfesional';
import Ventana from '../components/Ventana';

// Solapa Profesionales: alta, datos y baja de los profesionales. Son los mismos
// que se asignan en la agenda y en Equipo Cenein.
const inputClass =
  'rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100';
const botonPrimario = 'rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50';
const tarjeta = 'rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800';
const normalizar = (valor) => String(valor || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export default function Profesionales() {
  const [lista, setLista] = useState(null);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [tipo, setTipo] = useState('');
  const [abierto, setAbierto] = useState(null); // 'nuevo' o el id del profesional
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(async () => {
    try { setLista(await obtenerProfesionalesApi()); }
    catch (err) { setError(err.message || 'No se pudo cargar la lista de profesionales.'); setLista((l) => l || []); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const visibles = useMemo(() => (lista || []).filter((p) => (!tipo || (tipo === 'sin' ? !p.tipo : p.tipo === tipo))
    && normalizar(`${p.nombre} ${p.nombres} ${p.apellido} ${p.dni} ${p.email}`).includes(normalizar(busqueda).trim())), [lista, busqueda, tipo]);
  const elegido = abierto && abierto !== 'nuevo' ? (lista || []).find((p) => p.id === abierto) : null;

  const guardado = async (p) => {
    setMensaje(abierto === 'nuevo' ? `${p.nombre} se agregó. Ya se puede asignar en la agenda.` : `Datos de ${p.nombre} guardados.`);
    setAbierto(null);
    await cargar();
  };
  const eliminar = async (p) => {
    if (ocupado || !window.confirm(`¿Eliminar a ${p.nombre}? Se borran sus datos y su email, y sale de Equipo Cenein.`)) return;
    setOcupado(true); setError(''); setMensaje('');
    try {
      try {
        await eliminarProfesionalAgendaApi(p.id);
      } catch (err) {
        const casillas = err?.data?.casillas;
        if (err?.status !== 409 || !casillas) throw err;
        if (!window.confirm(`${p.nombre} figura en ${casillas} casilla${casillas === 1 ? '' : 's'} de la agenda. Si lo eliminás, esas casillas quedan sin este profesional (los pacientes se mantienen). ¿Eliminar igual?`)) return;
        await eliminarProfesionalAgendaApi(p.id, { forzar: true });
      }
      setAbierto(null);
      setMensaje(`${p.nombre} se eliminó.`);
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el profesional.');
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-slate-100">Profesionales</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Alta, datos y baja de los profesionales. Tocá uno para ver o cambiar sus datos.</p>
        </div>
        <button type="button" className={botonPrimario} onClick={() => { setAbierto('nuevo'); setMensaje(''); setError(''); }}>Nuevo profesional</button>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-[12rem] flex-1 text-xs font-bold text-slate-600 dark:text-slate-300">
          <span className="mb-1 block">Buscar</span>
          <input type="search" className={`${inputClass} w-full`} value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Nombre, apellido, DNI o email" autoComplete="off" />
        </label>
        <label className="text-xs font-bold text-slate-600 dark:text-slate-300">
          <span className="mb-1 block">Tipo</span>
          <select className={inputClass} value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="">Todos</option>
            {TIPOS_PROFESIONAL.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            <option value="sin">Sin completar</option>
          </select>
        </label>
      </div>

      {error && <p role="alert" className="rounded-xl bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">{error}</p>}
      {mensaje && <p role="status" className="rounded-xl bg-emerald-50 px-4 py-2 text-sm text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">{mensaje}</p>}

      {lista === null ? <p className="text-sm text-slate-500">Cargando profesionales…</p> : !visibles.length ? (
        <p className={`${tarjeta} p-6 text-sm text-slate-500`}>{lista.length ? 'No hay profesionales que coincidan.' : 'Todavía no hay profesionales. Agregá el primero.'}</p>
      ) : (
        <div className={`${tarjeta} overflow-x-auto`}>
          <table className="w-full min-w-[40rem] text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3">Profesional</th>
                <th className="px-4 py-3">DNI</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Tipo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {visibles.map((p) => {
                const completo = [p.apellido, p.nombres].filter(Boolean).join(', ');
                return (
                  <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                    <td className="px-4 py-2">
                      <button type="button" className="text-left font-semibold text-slate-800 hover:text-emerald-700 dark:text-slate-100" aria-label={`Abrir ${p.nombre}`}
                        onClick={() => { setAbierto(p.id); setMensaje(''); setError(''); }}>
                        {completo || p.nombre}
                      </button>
                      {completo && completo !== p.nombre && <span className="block text-xs text-slate-400">En la agenda: {p.nombre}</span>}
                    </td>
                    <td className="px-4 py-2 text-slate-600 dark:text-slate-300">{p.dni || '—'}</td>
                    <td className="px-4 py-2 text-slate-600 dark:text-slate-300">{p.email || '—'}</td>
                    <td className="px-4 py-2">
                      {p.tipo ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">{etiquetaTipo(p.tipo)}</span>
                        : <span className="text-xs text-amber-600">Sin completar</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {abierto && (abierto === 'nuevo' || elegido) && (
        <Ventana titulo={elegido ? elegido.nombre : 'Nuevo profesional'} alCerrar={() => setAbierto(null)} ancho="max-w-2xl">
          <section className={`${tarjeta} space-y-4 p-5`}>
            <FormularioProfesional key={abierto} profesional={elegido} alGuardado={guardado} alCancelar={() => setAbierto(null)} />
            {elegido && (
              <div className="border-t border-slate-100 pt-3 dark:border-slate-700">
                <button type="button" className="rounded-xl bg-rose-600 px-3 py-2 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-50"
                  disabled={ocupado} aria-label={`Eliminar a ${elegido.nombre}`} onClick={() => eliminar(elegido)}>Eliminar profesional</button>
              </div>
            )}
          </section>
        </Ventana>
      )}
    </div>
  );
}
